import {
  createContext,
  useContext,
  useMemo,
  useState,
  useRef,
  type ReactNode,
} from "react";
import AgoraRTC, {
  type IAgoraRTCClient,
  type IMicrophoneAudioTrack,
  type ICameraVideoTrack,
  type IAgoraRTCRemoteUser,
} from "agora-rtc-sdk-ng";
import { toast } from "sonner";
import { callConnectivityAPI } from "../../api/connectivityApis/call-connectivity.api";

export interface AgoraContextType {
  client: IAgoraRTCClient | null;
  localAudioTrack: IMicrophoneAudioTrack | null;
  localVideoTrack: ICameraVideoTrack | null;
  remoteUsers: IAgoraRTCRemoteUser[];
  isConnected: boolean;
  isMuted: boolean;
  isVideoOff: boolean;

  join: (
    channelName: string,
    rtcToken: string,
    isVideo: boolean,
    urlExpertCode?: string,
    sessionContext?: any
  ) => Promise<void>;

  leave: (channelName?: string) => Promise<void>;
  toggleMute: () => Promise<void>;
  toggleVideo: () => Promise<void>;
}

export const AgoraContext = createContext<AgoraContextType | null>(null);

export function useAgoraContext() {
  const context = useContext(AgoraContext);

  if (!context) {
    throw new Error(
      "useAgoraContext must be used inside AgoraProvider"
    );
  }

  return context;
}

export function AgoraProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<IAgoraRTCClient | null>(null);

  const clientRef = useRef<IAgoraRTCClient | null>(null);

  const [localAudioTrack, setLocalAudioTrack] =
    useState<IMicrophoneAudioTrack | null>(null);

  const [localVideoTrack, setLocalVideoTrack] =
    useState<ICameraVideoTrack | null>(null);

  const localAudioTrackRef =
    useRef<IMicrophoneAudioTrack | null>(null);

  const localVideoTrackRef =
    useRef<ICameraVideoTrack | null>(null);

  const [remoteUsers, setRemoteUsers] =
    useState<IAgoraRTCRemoteUser[]>([]);

  const [isConnected, setIsConnected] =
    useState(false);

  const [isMuted, setIsMuted] =
    useState(false);

  const [isVideoOff, setIsVideoOff] =
    useState(false);

  /*
   * ----------------------------------------------------------
   * RECORDING STATE
   * ----------------------------------------------------------
   */

  const localRecordingStartedRef =
    useRef(false);

  const recordingStartInProgressRef =
    useRef(false);

  const callStartTimeRef =
    useRef<number>(0);

  const currentChannelRef =
    useRef<string>("");

  const joiningRef =
    useRef(false);

  const APP_ID =
    import.meta.env.VITE_AGORA_APP_ID;

  const join = async (
    channelName: string,
    rtcToken: string,
    isVideo: boolean,
    urlExpertCode?: string,
    sessionContext?: any
  ) => {
    if (
      joiningRef.current ||
      clientRef.current ||
      isConnected
    ) {
      return;
    }

    joiningRef.current = true;

    try {
      AgoraRTC.setLogLevel(3);

      const agoraClient =
        AgoraRTC.createClient({
          mode: "rtc",
          codec: "vp8",
        });

      clientRef.current = agoraClient;
      setClient(agoraClient);

      currentChannelRef.current =
        channelName;

      const isExpert =
        !!localStorage.getItem("expertCode");

      /*
       * ----------------------------------------------------------
       * START RECORDING ONLY AFTER REMOTE PARTICIPANT JOINS
       * ----------------------------------------------------------
       *
       * Joining Agora locally does NOT mean that the consultation
       * has started.
       *
       * Recording must not start while the user is alone in the
       * waiting/signalling stage.
       *
       * The recording is started only after Agora tells us that
       * another participant has joined this channel.
       */

      const startRecordingWhenRemoteJoins =
        async () => {
          /*
           * Expert never starts the recording.
           *
           * Only the user starts the cloud recording because
           * the recording API belongs to the user-side flow.
           */

          if (
            isExpert ||
            localRecordingStartedRef.current ||
            recordingStartInProgressRef.current
          ) {
            return;
          }

          /*
           * Prevent duplicate API calls if multiple Agora events
           * arrive around the same time.
           */

          recordingStartInProgressRef.current =
            true;

          try {
            const userCode =
              localStorage.getItem("user_code") ||
              "unknown";

            const expertCode =
              urlExpertCode ||
              sessionContext?.expertCode ||
              "unknown";

            const intakeData =
              sessionContext?.intake?.dynamicFormData ||
              {};

            console.log(
              "[Recording] 👥 Remote Agora participant joined. Starting cloud recording..."
            );

            await callConnectivityAPI.startRecording(
              channelName,
              {
                userCode,
                expertCode,
                intakeData,
              }
            );

            localRecordingStartedRef.current =
              true;

            callStartTimeRef.current =
              Date.now();

            console.log(
              "[Recording] ✅ Cloud recording started after both Agora participants were present."
            );
          } catch (err) {
            console.error(
              "[Recording] ❌ Failed to start recording:",
              err
            );
          } finally {
            recordingStartInProgressRef.current =
              false;
          }
        };

      /*
       * ----------------------------------------------------------
       * REMOTE USER PUBLISHED
       * ----------------------------------------------------------
       */

      agoraClient.on(
        "user-published",
        async (user, mediaType) => {
          await agoraClient.subscribe(
            user,
            mediaType
          );

          if (
            mediaType === "audio" &&
            user.audioTrack
          ) {
            user.audioTrack.play();
          }

          setRemoteUsers(
            Array.from(
              agoraClient.remoteUsers
            )
          );
        }
      );

      /*
       * ----------------------------------------------------------
       * REMOTE USER UNPUBLISHED
       * ----------------------------------------------------------
       */

      agoraClient.on(
        "user-unpublished",
        () => {
          setRemoteUsers(
            Array.from(
              agoraClient.remoteUsers
            )
          );
        }
      );

      /*
       * ----------------------------------------------------------
       * REMOTE USER JOINED
       * ----------------------------------------------------------
       *
       * Recording starts only when another Agora participant
       * actually joins the same channel.
       */

      agoraClient.on(
        "user-joined",
        async () => {
          const users =
            Array.from(
              agoraClient.remoteUsers
            );

          setRemoteUsers(users);

          /*
           * There must actually be another participant.
           */

          if (users.length === 0) {
            return;
          }

          await startRecordingWhenRemoteJoins();
        }
      );

      /*
       * ----------------------------------------------------------
       * REMOTE USER LEFT
       * ----------------------------------------------------------
       */

      agoraClient.on(
        "user-left",
        () => {
          setRemoteUsers(
            Array.from(
              agoraClient.remoteUsers
            )
          );
        }
      );

      /*
       * ----------------------------------------------------------
       * JOIN AGORA
       * ----------------------------------------------------------
       *
       * IMPORTANT:
       *
       * We DO NOT start recording here.
       *
       * The user can remain alone in the channel while waiting
       * for the expert.
       */

      await agoraClient.join(
        APP_ID,
        channelName,
        rtcToken,
        null
      );

      /*
       * ----------------------------------------------------------
       * CREATE MICROPHONE
       * ----------------------------------------------------------
       */

      const audioTrack =
        await AgoraRTC.createMicrophoneAudioTrack();

      localAudioTrackRef.current =
        audioTrack;

      setLocalAudioTrack(
        audioTrack
      );

      /*
       * ----------------------------------------------------------
       * CREATE CAMERA
       * ----------------------------------------------------------
       */

      let videoTrack:
        ICameraVideoTrack | null = null;

      if (isVideo) {
        try {
          videoTrack =
            await AgoraRTC.createCameraVideoTrack();

          localVideoTrackRef.current =
            videoTrack;

          setLocalVideoTrack(
            videoTrack
          );
        } catch (camErr) {
          console.error(
            "Camera access denied or unavailable:",
            camErr
          );

          toast.error(
            "Camera access denied! Initializing with audio only."
          );
        }
      }

      /*
       * ----------------------------------------------------------
       * PUBLISH LOCAL TRACKS
       * ----------------------------------------------------------
       */

      const tracksToPublish =
        videoTrack
          ? [audioTrack, videoTrack]
          : [audioTrack];

      await agoraClient.publish(
        tracksToPublish
      );

      /*
       * ----------------------------------------------------------
       * CONNECTED STATE
       * ----------------------------------------------------------
       */

      setIsConnected(true);
      setIsMuted(false);
      setIsVideoOff(!videoTrack);
    } catch (error: any) {
      if (
        error?.message?.includes("ABORTED") ||
        error?.code === "OPERATION_ABORTED"
      ) {
        return;
      }

      console.error(
        "[Agora Provider]",
        error
      );

      toast.error(
        "Failed to connect to secure communication server."
      );

      throw error;
    } finally {
      joiningRef.current = false;
    }
  };

  /*
   * ----------------------------------------------------------
   * LEAVE AGORA
   * ----------------------------------------------------------
   */

  const leave = async () => {
    if (!clientRef.current) {
      return;
    }

    const isExpert =
      !!localStorage.getItem("expertCode");

    const channelName =
      currentChannelRef.current;

    /*
     * Stop recording only if:
     *
     * 1. This is the user.
     * 2. Recording actually started.
     *
     * If the user leaves while still waiting for the expert,
     * recordingStartedRef will be false and no stop API call
     * will be made.
     */

    if (
      channelName &&
      localRecordingStartedRef.current &&
      !isExpert
    ) {
      const elapsed =
        Date.now() -
        callStartTimeRef.current;

      /*
       * Keep the existing minimum recording duration.
       */

      const minimumRecordTime =
        15000;

      const delay =
        Math.max(
          0,
          minimumRecordTime - elapsed
        );

      setTimeout(async () => {
        try {
          await callConnectivityAPI.stopRecording(
            channelName
          );
        } catch (err) {
          console.error(
            "Failed to stop recording:",
            err
          );
        }
      }, delay);
    }

    /*
     * ----------------------------------------------------------
     * CLOSE AUDIO TRACK
     * ----------------------------------------------------------
     */

    if (
      localAudioTrackRef.current
    ) {
      localAudioTrackRef.current.stop();
      localAudioTrackRef.current.close();

      localAudioTrackRef.current =
        null;

      setLocalAudioTrack(null);
    }

    /*
     * ----------------------------------------------------------
     * CLOSE VIDEO TRACK
     * ----------------------------------------------------------
     */

    if (
      localVideoTrackRef.current
    ) {
      localVideoTrackRef.current.stop();
      localVideoTrackRef.current.close();

      localVideoTrackRef.current =
        null;

      setLocalVideoTrack(null);
    }

    /*
     * ----------------------------------------------------------
     * LEAVE AGORA CHANNEL
     * ----------------------------------------------------------
     */

    try {
      await clientRef.current.leave();
    } catch (err) {
      console.error(
        "Error leaving Agora:",
        err
      );
    }

    /*
     * ----------------------------------------------------------
     * RESET STATE
     * ----------------------------------------------------------
     */

    clientRef.current = null;

    setClient(null);

    setIsConnected(false);

    setRemoteUsers([]);

    setIsMuted(false);

    setIsVideoOff(false);

    localRecordingStartedRef.current =
      false;

    recordingStartInProgressRef.current =
      false;

    callStartTimeRef.current =
      0;

    currentChannelRef.current =
      "";

    joiningRef.current =
      false;
  };

  /*
   * ----------------------------------------------------------
   * MUTE / UNMUTE
   * ----------------------------------------------------------
   */

  const toggleMute = async () => {
    if (localAudioTrack) {
      const nextMuteState =
        !isMuted;

      await localAudioTrack.setMuted(
        nextMuteState
      );

      setIsMuted(
        nextMuteState
      );
    }
  };

  /*
   * ----------------------------------------------------------
   * VIDEO ON / OFF
   * ----------------------------------------------------------
   */

  const toggleVideo = async () => {
    if (localVideoTrack) {
      const nextVideoState =
        !isVideoOff;

      await localVideoTrack.setMuted(
        nextVideoState
      );

      setIsVideoOff(
        nextVideoState
      );
    }
  };

  /*
   * ----------------------------------------------------------
   * CONTEXT VALUE
   * ----------------------------------------------------------
   */

  const value =
    useMemo<AgoraContextType>(
      () => ({
        client,
        localAudioTrack,
        localVideoTrack,
        remoteUsers,
        isConnected,
        isMuted,
        isVideoOff,
        join,
        leave,
        toggleMute,
        toggleVideo,
      }),
      [
        client,
        localAudioTrack,
        localVideoTrack,
        remoteUsers,
        isConnected,
        isMuted,
        isVideoOff,
      ]
    );

  return (
    <AgoraContext.Provider
      value={value}
    >
      {children}
    </AgoraContext.Provider>
  );
}